import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Col, Row, Spin, Flex } from 'antd'
import { EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons'
import {Restaurant} from "../models/Restaurant";
import {RestaurantService} from "../services/RestaurantService";
import {OrderService} from "../services/OrderService";
import TopNavigation, { HEADER_HEIGHT } from "./snipets/TopNavigation";

const getWorkingStatus = (working_hours?: { open?: string; close?: string }): string => {
    if (!working_hours?.open || !working_hours?.close) return 'Unavailable'

    const now = new Date()
    const toMinutes = (time: string): number => {
        const [h, m] = time.split(':').map(Number)
        return h * 60 + m
    }

    const open         = working_hours.open.substring(0, 5)
    const close        = working_hours.close.substring(0, 5)
    const nowMinutes   = now.getHours() * 60 + now.getMinutes()
    const openMinutes  = toMinutes(open)
    const closeMinutes = toMinutes(close)
    const timeRange    = `${open}–${close}`

    if (nowMinutes < openMinutes || nowMinutes >= closeMinutes) return `${timeRange} Closed`
    if (closeMinutes - nowMinutes <= 60)                        return `${timeRange} Closing soon`
    return `${timeRange} Open`
}

const getStatusColor = (status: string): string => {
    if (status.includes('Closing soon')) return '#FF9500'
    if (status.includes('Closed') || status.includes('Unavailable')) return '#FF3B30'
    return '#00AF66'
}

const Restaurants: React.FC = () => {

    const { latitude, longitude } = useParams<{ latitude: string; longitude: string }>()
    const navigate = useNavigate()
    const [restaurants, setRestaurants] = useState<Restaurant[]>([])
    const [loading, setLoading] = useState(false)
    const [checking, setChecking] = useState(true)

    // Mirrors HomeScreen's initialize(): two independent checks run concurrently
    // on arrival. Only stop the loader (and load restaurants) once BOTH have
    // resolved and neither triggered a redirect.
    useEffect(() => {
        let navigated = false
        let activeOrderChecked = false
        let ratingChecked = false

        const maybeStopChecking = () => {
            if (!navigated && activeOrderChecked && ratingChecked) {
                setChecking(false)
            }
        }

        OrderService.getActiveOrdersByUser()
            .then((activeOrders) => {
                if (activeOrders.length > 0 && !navigated) {
                    navigated = true
                    navigate('/orderTrackingPage')
                }
            })
            .catch(() => {})
            .finally(() => {
                activeOrderChecked = true
                maybeStopChecking()
            })

        OrderService.checkRating()
            .then((response) => {
                if (response.status === 303 && !navigated) {
                    navigated = true
                    navigate('/rateOrderPage')
                }
            })
            .catch(() => {})
            .finally(() => {
                ratingChecked = true
                maybeStopChecking()
            })
    }, [navigate])

    useEffect(() => {
        if (checking) return

        setLoading(true)
        RestaurantService.getNearestRestaurants(latitude, longitude).then((restaurants: Restaurant[]) => {
            setRestaurants(restaurants)
            console.log(restaurants)
        }).finally(() => setLoading(false))
    }, [checking]);

    if (checking) {
        return (
            <Flex align="center" justify="center" style={{ minHeight: '100dvh' }}>
                <Spin size="large" />
            </Flex>
        )
    }

    return (
        <>

            <TopNavigation />

            <div style={{ padding: 'clamp(16px, 4vw, 24px)', paddingTop: HEADER_HEIGHT + 16, maxWidth: 1200, margin: '0 auto' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Row gutter={[16, 16]}>
                        {restaurants.map((restaurant) => {
                            const status = getWorkingStatus(restaurant.working_hours)
                            return (
                                <Col key={restaurant.id} xs={24} sm={12} md={8} lg={6}>
                                    <Card
                                        hoverable
                                        onClick={() => navigate(`/restaurantMenuPage/${restaurant.id}`)}
                                        styles={{ body: { padding: '16px 16px 12px' } }}
                                    >
                                        <div style={{ position: 'relative', margin: '-16px -16px 20px' }}>
                                            {restaurant.banner_url
                                                ? <img src={restaurant.banner_url} alt={restaurant.name} style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: '8px 8px 0 0' }} />
                                                : <div style={{ height: 140, background: '#E6F7EF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, borderRadius: '8px 8px 0 0' }}>🍽️</div>
                                            }

                                            <div style={{
                                                position: 'absolute', top: 10, right: 10,
                                                background: getStatusColor(status),
                                                borderRadius: 8, padding: '3px 10px',
                                                color: '#fff', fontSize: 12, fontWeight: 700,
                                            }}>
                                                {status}
                                            </div>

                                            <div style={{
                                                position: 'absolute', bottom: -20, left: 16,
                                                width: 52, height: 52, borderRadius: 14,
                                                border: '2.5px solid #fff', overflow: 'hidden',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                background: '#fff',
                                            }}>
                                                {restaurant.logo_url && (
                                                    <img src={restaurant.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                )}
                                            </div>
                                        </div>

                                        <div style={{ fontWeight: 700, fontSize: 16, color: '#1A1A2E', marginBottom: 4 }}>
                                            {restaurant.name}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#8F9BB3', fontSize: 13 }}>
                                            <span><EnvironmentOutlined style={{ marginRight: 4 }} />{restaurant.distance ?? '—'}</span>
                                            <span><ClockCircleOutlined style={{ marginRight: 4 }} />20–35 min</span>
                                        </div>
                                    </Card>
                                </Col>
                            )
                        })}
                    </Row>
                )}
            </div>

        </>
    )
}

export default Restaurants